// O cliente como ele aparece no filtro da Busca Avançada.
//
// ## Por que isto é um arquivo
//
// A Busca Avançada passou a aceitar MAIS DE UM cliente (ponto 11 da Gisele,
// continuação: "não precisar criar vários Kanbans só para mudar a combinação
// de filtros"). Isso trouxe duas perguntas que antes não existiam, porque com
// um cliente só não havia o que comparar nem o que listar:
//
//   1. este cliente já está na lista?
//   2. que texto vai no chip?
//
// As duas são respondidas em dois lugares -- a tela, que desenha, e o filtro,
// que decide o que mostrar --, e é exatamente o padrão de duas fontes que já
// deixou a busca do Histórico com duas cópias divergentes. Uma delas
// procurava uma coluna removida em 2023 e derrubava a tela com 500.

/**
 * O que IDENTIFICA o cliente, em texto -- ou vazio quando nada o identifica.
 *
 * Razão social, nome fantasia, nome do contato e, por último, o registro (o
 * número que a pessoa digitou para achar o cliente). É a mesma ordem que
 * `chamadoDoCliente` usa para procurar no chamado: se as duas divergirem, o
 * chip diz um nome e o filtro procura outro, e a tela fica vazia sem explicar
 * por quê.
 *
 * Vazio é resposta legítima, e é por isso que esta função existe separada do
 * rótulo: o rótulo nunca pode ser vazio (precisa escrever ALGUMA coisa no
 * chip), e identidade vazia não pode casar com identidade vazia.
 *
 * @param {object|null} cliente
 * @returns {string}
 */
function identidadeDoCliente(cliente) {
    if (!cliente) return '';

    const nome =
        cliente.company?.business_name || cliente.company?.name || cliente.name || '';

    if (nome) return nome;

    return cliente.code != null ? String(cliente.code).trim() : '';
}

/**
 * O texto do chip.
 *
 * @param {object|null} cliente
 * @returns {string}
 */
export function rotuloDoCliente(cliente) {
    const identidade = identidadeDoCliente(cliente);

    if (identidade) return identidade;
    if (!cliente) return '';

    return cliente.id != null ? `Cliente #${cliente.id}` : 'Cliente';
}

/**
 * São o mesmo cliente?
 *
 * Por `id` quando os dois têm; senão pela identidade em texto. O `id` sozinho
 * não bastaria: a busca devolve ora o contato, ora a empresa, e o mesmo
 * registro pode voltar sem `id` de cliente. A identidade sozinha também não:
 * dois contatos diferentes da mesma empresa têm a mesma razão social, e
 * compará-los por texto faria o segundo nunca entrar na lista -- a pessoa
 * clicaria e nada aconteceria.
 *
 * Identidade VAZIA não casa com identidade vazia: pelo rótulo os dois seriam
 * "Cliente", e o segundo sumiria.
 *
 * @param {object|null} a
 * @param {object|null} b
 * @returns {boolean}
 */
export function mesmoCliente(a, b) {
    if (!a || !b) return false;

    if (a.id != null && b.id != null) return String(a.id) === String(b.id);

    const identidadeA = identidadeDoCliente(a);

    return identidadeA !== '' && identidadeA === identidadeDoCliente(b);
}
